/**
 * Typed API client for the editable client-document wording
 * (GET /admin/settings, PATCH /admin/settings/{key}). super_admin only.
 *
 * The API only exposes keys under the `documents.` prefix; anything else is
 * answered with 403.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export interface PlatformSetting {
  key: string;
  value: string;
  /** 'string' | 'int' | 'bool' | 'json' — the API validates a write against it. */
  value_type: string;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
}

interface SettingListResponse {
  items: PlatformSetting[];
}

/** The only prefix the API lets a super_admin read or write from the portal. */
export const DOCUMENT_SETTINGS_PREFIX = 'documents.';

class AdminSettingsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  private readonly base = '/api/v1/admin/settings';

  async list(prefix: string = DOCUMENT_SETTINGS_PREFIX): Promise<PlatformSetting[]> {
    const res = await this.api.get<SettingListResponse>(this.base, { params: { prefix } });
    return res.data.items;
  }

  /** Replace one value. The API validates it against the row's type and audits the old value. */
  async update(key: string, value: string): Promise<PlatformSetting> {
    const res = await this.api.patch<PlatformSetting>(
      `${this.base}/${encodeURIComponent(key)}`,
      { value },
    );
    return res.data;
  }
}

export default new AdminSettingsService();
